// Nombres de campos del backend tal como los ve el usuario
const ETIQUETAS_CAMPOS = {
  email: "Correo electrónico",
  identificacion: "Identificación",
  tipo_identificacion: "Tipo de identificación",
  nombres: "Nombres",
  apellidos: "Apellidos",
  celular: "Celular",
  direccion: "Dirección",
  fecha_nacimiento: "Fecha de nacimiento",
  causas: "Causas",
  descripcion: "Descripción",
  sede: "Sede",
  tipo_proceso: "Tipo de proceso",
  finalidad: "Finalidad",
  valor: "Valor",
  nombre: "Nombre",
  radicado: "Radicado",
  juzgado: "Juzgado",
  demandante: "Demandante",
  demandado: "Demandado",
  capital: "Capital",
  acreedor: "Acreedor",
  fecha: "Fecha",
  hora: "Hora",
  non_field_errors: "",
};

// Mensajes según el código HTTP cuando el backend no envía una explicación
const MENSAJES_POR_ESTADO = {
  400: "Los datos enviados no son válidos. Revise la información e intente de nuevo.",
  401: "Su sesión expiró o no es válida. Inicie sesión nuevamente.",
  403: "No tiene permisos para realizar esta acción.",
  404: "No se encontró la información solicitada. Es posible que haya sido eliminada; actualice la página.",
  405: "La operación no está permitida.",
  409: "La información fue modificada por otra persona. Actualice la página e intente de nuevo.",
  413: "El archivo es demasiado grande para ser procesado.",
  429: "Se realizaron demasiadas solicitudes seguidas. Espere un momento e intente de nuevo.",
  500: "Ocurrió un error interno en el servidor. Si el problema continúa, informe a soporte.",
  502: "El servidor no está disponible en este momento. Intente de nuevo en unos minutos.",
  503: "El servidor no está disponible en este momento. Intente de nuevo en unos minutos.",
  504: "El servidor tardó demasiado en responder. Intente de nuevo en unos minutos.",
};

const etiquetaCampo = (campo) => {
  if (Object.prototype.hasOwnProperty.call(ETIQUETAS_CAMPOS, campo)) return ETIQUETAS_CAMPOS[campo];
  const texto = campo.replace(/_/g, " ");
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const textoDeValor = (valor) => {
  if (typeof valor === "string") return valor;
  if (Array.isArray(valor)) return valor.map(textoDeValor).filter(Boolean).join(" ");
  if (valor && typeof valor === "object") return mensajeDeCampos(valor);
  return "";
};

// Errores de validación por campo de DRF: { campo: ["mensaje"] }
const mensajeDeCampos = (data) =>
  Object.entries(data)
    .map(([campo, valor]) => {
      const texto = textoDeValor(valor);
      if (!texto) return "";
      const etiqueta = etiquetaCampo(campo);
      return etiqueta ? `${etiqueta}: ${texto}` : texto;
    })
    .filter(Boolean)
    .join(" | ");

// Motivo explicado por el backend (sin contexto de la acción)
const motivoDelBackend = (data) => {
  if (!data) return "";
  if (typeof data === "string") {
    const texto = data.trim();
    // Respuestas HTML (páginas de error del servidor) no se muestran al usuario
    return texto && !texto.startsWith("<") && texto.length < 500 ? texto : "";
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) return "";
  if (typeof data !== "object") return "";
  for (const clave of ["error", "detail", "message", "mensaje"]) {
    const texto = textoDeValor(data[clave]);
    if (texto) return texto;
  }
  const campos = Object.fromEntries(Object.entries(data).filter(([clave]) => clave !== "success"));
  return mensajeDeCampos(campos);
};

const unirMensajes = (contexto, motivo) => {
  if (!contexto) return motivo;
  if (!motivo) return contexto;
  // "Error al guardar. Por favor, intenta nuevamente." → "Error al guardar: <motivo>"
  const base = contexto
    .trim()
    .replace(
      /[.,\s]*(por favor,?\s*)?(intent[ae]\s+(nuevamente|de nuevo)|vuelva a intentarlo)[.!\s]*$/i,
      ""
    )
    .replace(/[.:,\s]+$/, "");
  return `${base}: ${motivo}`;
};

/**
 * Mensaje claro de un error de Axios.
 * - Si el backend explica el motivo, se muestra junto al contexto: "Error al guardar: El correo ya existe..."
 * - Si no, se explica según el tipo de falla (sin conexión, sesión, permisos, servidor...).
 * @param {object} error Error capturado de Axios.
 * @param {string} mensajePorDefecto Contexto de la acción que falló (ej: "Error al guardar el parámetro").
 */
export const obtenerMensajeError = (error, mensajePorDefecto = "Ocurrió un error") => {
  if (!error) return mensajePorDefecto;

  // Sin respuesta del servidor: conexión, tiempo de espera o CORS
  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      return unirMensajes(
        mensajePorDefecto,
        "El servidor tardó demasiado en responder. Verifique su conexión e intente de nuevo."
      );
    }
    if (error.request || error.message === "Network Error") {
      return unirMensajes(
        mensajePorDefecto,
        "No fue posible conectarse con el servidor. Verifique su conexión a internet e intente de nuevo."
      );
    }
    // Errores de programación del navegador: su texto técnico no le sirve al usuario
    if (
      error instanceof TypeError ||
      error instanceof ReferenceError ||
      error instanceof SyntaxError ||
      error instanceof RangeError
    ) {
      return mensajePorDefecto;
    }
    // Errores propios lanzados con un mensaje para el usuario (throw new Error("..."))
    return error.message ? unirMensajes(mensajePorDefecto, error.message) : mensajePorDefecto;
  }

  const { status, data } = error.response;
  const motivo = motivoDelBackend(data);
  if (motivo) return unirMensajes(mensajePorDefecto, motivo);
  return unirMensajes(
    mensajePorDefecto,
    MENSAJES_POR_ESTADO[status] || `Código de respuesta ${status}.`
  );
};

/**
 * Igual que obtenerMensajeError, pero también lee el motivo cuando la petición pidió un archivo
 * (responseType: "blob") y el backend respondió un error en JSON.
 */
export const obtenerMensajeErrorAsync = async (error, mensajePorDefecto = "Ocurrió un error") => {
  const data = error?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const texto = await data.text();
      const contenido = texto.trim().startsWith("{") ? JSON.parse(texto) : texto;
      const motivo = motivoDelBackend(contenido);
      if (motivo) return unirMensajes(mensajePorDefecto, motivo);
    } catch {
      // Si el contenido no es legible se usa el mensaje por código HTTP
    }
  }
  return obtenerMensajeError(error, mensajePorDefecto);
};
